import React, { useEffect } from "react";
import {
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiTag,
  FiX,
} from "react-icons/fi";

export default function PdfViewerModal({ item, onClose }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  if (!item) return null;

  const pdfUrl = item.url ? `${item.url}#toolbar=1&navpanes=0&scrollbar=1` : "";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/75 px-3 py-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300">
                <FiFileText className="h-5 w-5" />
              </span>

              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold text-slate-950 dark:text-white">
                  {item.title}
                </h2>

                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  {item.dateLabel && <span>Publié le {item.dateLabel}</span>}

                  {item.category && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                      <FiTag className="h-3 w-3" />
                      {item.category}
                    </span>
                  )}

                  {item.fileSize && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                      {item.fileSize}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-2">
            {item.url && (
              <>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-200 px-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-900"
                >
                  <FiExternalLink className="h-4 w-4" />
                  Ouvrir
                </a>

                <a
                  href={item.url}
                  download={item.downloadName}
                  className="inline-flex h-10 items-center gap-2 rounded-2xl bg-[#1277B9] px-3 text-sm font-bold text-white transition hover:bg-[#0f659d]"
                >
                  <FiDownload className="h-4 w-4" />
                  Télécharger
                </a>
              </>
            )}

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-950 text-white transition hover:bg-slate-700"
              aria-label="Fermer"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_300px]">
          <div className="min-h-0 bg-slate-100 dark:bg-slate-900">
            {pdfUrl ? (
              <iframe
                title={item.title}
                src={pdfUrl}
                className="h-full min-h-[65vh] w-full border-0"
              />
            ) : (
              <div className="flex h-full items-center justify-center p-8 text-center text-sm font-semibold text-slate-500">
                Impossible de charger ce document.
              </div>
            )}
          </div>

          <aside className="hidden border-l border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950 lg:block">
            <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Informations
            </h3>

            <div className="mt-5 space-y-5 text-sm">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                  Titre
                </p>
                <p className="mt-1 font-bold text-slate-950 dark:text-white">
                  {item.title}
                </p>
              </div>

              {item.dateLabel && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Publication
                  </p>
                  <p className="mt-1 text-slate-700 dark:text-slate-300">
                    {item.dateLabel}
                  </p>
                </div>
              )}

              {item.category && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Catégorie
                  </p>
                  <p className="mt-1 text-slate-700 dark:text-slate-300">
                    {item.category}
                  </p>
                </div>
              )}

              {item.tags && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Mots-clés
                  </p>
                  <p className="mt-1 leading-7 text-slate-700 dark:text-slate-300">
                    {item.tags}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                  Fichier
                </p>
                <p className="mt-1 break-all text-xs text-slate-600 dark:text-slate-400">
                  {item.originalName || item.fileName || item.downloadName}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}